import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { withLang } from "./withLang";
import { auth } from "./firebaseConfig"
import { connect, useSelector } from "react-redux"
import { setOrganizer } from "../features/organizer/organizerSlice"
import { signInWithCustomToken } from "firebase/auth"
import { store } from "../reducers/store"
import { readStoredJson } from "../functions/safeStorage"
const params = new URLSearchParams(window.location.search)
const platform = params.get("platform")
// module scope — a corrupt stored value here would throw during boot
const localStorageOrganizer = readStoredJson("organizer")

function HandleError(err) {
  console.log("login HandleError: ", err)
}

async function RunAuthActions(result, args, api, extraOptions) {
  console.log("RunAuthActions")

  try {
    console.log("organizerApi auth.currentUser: ", auth)
    const getToken = async () => {
      return await auth.currentUser.getIdToken(true)
    }
    const updatedToken = await getToken().catch(HandleError)
    console.log(" organizerApi updatedToken: ", updatedToken)
    if (updatedToken) {
      //Store new token
      api.dispatch(setOrganizer({ bearerToken: updatedToken }))
      // retry the initial query
      result = await baseQuery(args, api, extraOptions)
      return result
    } else {
      // SCO-1856: both shells now answer a token request through this callback,
      // so it has to be registered BEFORE either request goes out. Assigned only
      // on the native branches — on web, useFirebaseAuth owns setJwtExternal.
      const signInWithNativeToken = async (token) => {
        try {
          const jwtSignIn = await signInWithCustomToken(auth, token)
          console.log("native jwtSignIn: ", jwtSignIn)
          api.dispatch(
            setOrganizer({ bearerToken: jwtSignIn.user.accessToken })
          )
          // setRefreshOrganizerScreen();
        } catch (error) {
          console.log("setJwtExternal ERROR: ", error)
        }
      }
      if (platform === "ios") {
        window.setJwtExternal = signInWithNativeToken
        window.webkit.messageHandlers.requestAccessToken.postMessage("request")
      } else if (platform === "android") {
        // Newer shells mint lazily and answer via setJwtExternal; their
        // synchronous getAccessToken() returns "", so detect that first.
        if (typeof window.Android?.requestAccessToken === "function") {
          window.setJwtExternal = signInWithNativeToken
          window.Android.requestAccessToken()
        } else {
          async function LoginAndroid() {
            console.log("LoginAndroid")
            //Android native login function
            // Holds a CUSTOM token (one signInWithCustomToken), not an ID token.
            const customTokenFromApp = window.Android.getAccessToken()
            if (customTokenFromApp) {
              const jwtSignIn = await signInWithCustomToken(auth, customTokenFromApp)
              api.dispatch(
                setOrganizer({ bearerToken: jwtSignIn.user.accessToken })
              )
            }
          }
          // SCO-2048: the outer try/catch can't catch this async rejection
          LoginAndroid().catch((error) => {
            console.log("LoginAndroid failed: ", error)
          })
        }
      } else {
        //Platform is Web without a user
        console.log("platform is web and !user: ")
        // console.log("localStorageOrganizer from auth: ", localStorageOrganizer);
        api.dispatch(setOrganizer(localStorageOrganizer))
        // console.log("okay retrying auth again with new baseQuery");
        result = await baseQuery(args, api, extraOptions)
        return result
      }
    }
  } catch (err) {
    console.log("Attempt to fire native login fail")
    if (!platform) {
      const path = new URL(window.location.href).origin
      const sport = new URL(window.location.href).pathname.split("/")[2]
      window.location.replace(`${path}/v2/${sport}/organizer`)
    }
  }
}

const baseQuery = withLang(
  fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_BASE_URL}/`,
    prepareHeaders: async (headers, { getState, dispatch }) => {
      const getToken = async () => {
        return await auth.currentUser.getIdToken(true);
      };
      const updatedToken = await getToken().catch(HandleError);
      // console.log("updatedToken: ", updatedToken);
      const token = getState().organizer.bearerToken;
      const tokenToUse = updatedToken
        ? updatedToken
        : token
          ? token
          : localStorageOrganizer && localStorageOrganizer.bearerToken
            ? localStorageOrganizer.bearerToken
            : token;
      // console.log("tokenToUse: ", tokenToUse);
      headers.set("authorization", `Bearer ${tokenToUse}`);
      return headers;
    },
  }),
);

// Exported so other secure APIs (sharedCourtsApi) reuse this auth + reauth path
// rather than duplicating the token handling.
export const baseQueryWithReauth = async (args, api, extraOptions) => {
  const globalState = store.getState();
  console.log("globalState: ", globalState);
  let result;
  if (!globalState.organizer.isLogout) {
    console.log("baseQueryWithReauth running!");
    if (!platform && !globalState.organizer.bearerToken) {
      console.log("baseQueryWithReauth: there is no token !");
      api.dispatch(setOrganizer({...localStorageOrganizer, isGodPower: params.get('godpower') === 'true'}));
    }
    result = await baseQuery(args, api, extraOptions);
    console.log("baseQueryWithReauth result: ", result);
    if (result.error && result.error.status > 399) {
      console.log("baseQueryWithReauth running 400! auth: ", auth);
      // try to get a new token
      RunAuthActions(result, args, api, extraOptions);
    }
    return result;
  }
  return result;
};

export const organizerApi = createApi({
  reducerPath: "organizerApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getProfileSingle: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/profile`,
        method: "POST",
        body: body,
      }),
    }),
    updateProfileSingle: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/update-profile`,
        method: "POST",
        body: body,
      }),
    }),
    getStats: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/charts/stats`,
        method: "POST",
        body: body,
      }),
    }),
    // The Stats page's per-event rows (SCO-2755); same body as getStats. Fails as HTTP 200 + statusCode 1.
    getStatsTournaments: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/stats/tournaments`,
        method: "POST",
        body: body,
      }),
    }),
    // Quick Tournament Wizard NL parse (SCO-1749): a natural-language description
    // → curated draft config. body: { description, sport, timezone, nowISO }.
    parseWizard: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/wizard/parse`,
        method: "POST",
        body: body,
      }),
    }),
    updateProfileImage: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/update-profile-image`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-2538: sponsor ads go to Storage server-side; the doc holds only the URL
    uploadAd: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/ads/upload`,
        method: "POST",
        body: body,
      }),
    }),
    deleteAd: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/ads/delete`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-3182: the Seasons admin's API keys (seasonsSecure, same Firebase token). Errors are HTTP 200 + statusCode ≠ 0.
    listApiKeys: builder.mutation({
      query: () => ({ url: `seasonsSecure/apikeys/list`, method: "POST", body: {} }),
    }),
    createApiKey: builder.mutation({
      query: (body) => ({ url: `seasonsSecure/apikeys/create`, method: "POST", body }),
    }),
    revokeApiKey: builder.mutation({
      query: (body) => ({ url: `seasonsSecure/apikeys/revoke`, method: "POST", body }),
    }),
    getApiKeyUsage: builder.mutation({
      query: (body) => ({ url: `seasonsSecure/apikeys/usage`, method: "POST", body }),
    }),
    // SCO-2551: season logo to Storage server-side; returns the URL (merges onto the doc if it exists)
    uploadSeasonLogo: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/seasons/upload-logo`,
        method: "POST",
        body: body,
      }),
    }),
    playerStats: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/player-stats`,
        method: "POST",
        body: body,
      }),
    }),
    updateTournamentTemplate: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/template/update`,
        method: "POST",
        body: body,
      }),
    }),
    updateTournamentForm: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/tournament/update`,
        method: "POST",
        body: body,
      }),
    }),
    getTournamentForm: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/tournament/get`,
        method: "POST",
        body: body,
      }),
    }),
    getTournamentFormExtraInfo: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/tournament/extra-info`,
        method: "POST",
        body: body,
      }),
    }),
    getTournamentFormTimezoneLookup: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/tournament/timezone-lookup`,
        method: "POST",
        body: body,
      }),
    }),
    getTournamentAlerts: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/pushalerts/get`,
        method: "POST",
        body: body,
      }),
    }),
    sendTournamentAlert: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/pushalerts/send`,
        method: "POST",
        body: body,
      }),
    }),
    checkAlertCount: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/pushalerts/check`,
        method: "POST",
        body: body,
      }),
    }),
    getTournaments: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/tournaments`,
        method: "POST",
        body: body,
      }),
    }),
    deleteOrganizerAccount: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/profile/delete`,
        method: "POST",
        body: body,
      }),
    }),
    addExcludedPlayer: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/excludedplayers/add`,
        method: "POST",
        body: body,
      }),
    }),
    // The ban list (Players 2027, SCO-2756): the body the legacy grid sends; every ban, newest first.
    getExcludedPlayers: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/excludedplayers/get`,
        method: "POST",
        body: body,
      }),
    }),
    deleteExcludedPlayer: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/excludedplayers/delete`,
        method: "POST",
        body: body,
      }),
    }),
    getSeason: builder.mutation({
      query: (body) => ({
        url: `connect/organizer/grid/get-season`,
        method: "POST",
        body: body,
      }),
    }),
    endOfYearOrganizer: builder.mutation({
      query: (body) => ({
        url: `apiv2/organizer/thatsawrap`,
        method: "POST",
        body: body,
      }),
    }),
    sendGodPowerAlert: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/godpoweralert`,
        method: "POST",
        body: body,
      }),
    }),
    uploadVenuePhoto: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/venue/upload-photo`,
        method: "POST",
        body: body,
      }),
    }),
    listVenuePhotos: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/venue/list-photos`,
        method: "POST",
        body: body,
      }),
    }),
    deleteVenuePhoto: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/venue/delete-photo`,
        method: "POST",
        body: body,
      }),
    }),
    // Custom dashboards CRUD — single endpoint, action in payload (see SCO-1698).
    // body: { action: "list" | "create" | "rename" | "update" | "delete" | "clone" | "tournaments", ...payload }
    manageDashboards: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/dashboards/manage`,
        method: "POST",
        body: body,
      }),
    }),
    // God Power organizer search (SCO-1708). body: { accountid, q } where q matches
    // display name / email / club name. Returns { rows: [{ rowid, organizerName,
    // organizerEmail, organizerClub }] }. Select a row to load that organizer's
    // tournaments (same as the ?uid= URL param).
    searchOrganizers: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/search`,
        method: "POST",
        body: body,
      }),
    }),
    // Add/check in one person to a tournament roster (SCO-1881, reused by the
    // quick-wizard player flush SCO-1939). body: { accountid, gameid, requestId,
    // mode?, firstName?/lastName?/displayName, email?, teamName?, teamId?,
    // playerUser?, source }. Idempotent per requestId.
    checkinAdd: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/checkin/add`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-2374: the beacon's auto check-in switch. Lives on the mobile
    // function (same Bearer middleware); body: { accountid, gameid, auto }.
    nearbySettings: builder.mutation({
      query: (body) => ({
        url: `mobile/nearby/settings`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-2364 contract: the organizer's explicit OFF ends the announce lease
    // now instead of drifting 180s. Native lifecycle stops never call this.
    // body: { gameId }
    nearbyStop: builder.mutation({
      query: (body) => ({
        url: `mobile/nearby/stop`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-2374: a queued join resolved from the wizard (organizer/checkin/add
    // doesn't clear the queue itself). body: { accountid, gameid, queueId }
    nearbyDequeue: builder.mutation({
      query: (body) => ({
        url: `mobile/nearby/dequeue`,
        method: "POST",
        body: body,
      }),
    }),
    // Club-player name search (same endpoint as the organizer Players grid).
    // body: { sport, clubAccount, search, startRow, endRow, timezone }
    searchClubPlayers: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/players`,
        method: "POST",
        body: body,
      }),
    }),
    // Remove one LIVE roster entry (SCO-2028, the write-through wizard's undo).
    // body: { accountid, gameid, object: "players"|"teams", recordId, slot? } —
    // players delete outright; teams+slot clear that slot (team deleted when the
    // last occupant leaves); teams without slot delete whole. Already-gone
    // records return success (action "alreadyRemoved"), so retries are safe.
    checkinRemove: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/checkin/remove`,
        method: "POST",
        body: body,
      }),
    }),
    // Check in one pre-reg record (playerspre→players / teamspre→teams) by doc
    // id — SCO-1881. body: { accountid, gameid, requestId?, object, recordId }.
    checkinPrereg: builder.mutation({
      query: (body) => ({
        url: `apisecurev2/organizer/checkin/prereg`,
        method: "POST",
        body: body,
      }),
    }),
    // The legacy admin's blind-draw randomize (same engine, SCO-1939 quick
    // flow). body: { accountid, gameid, randomA, randomB, randomC } — arrays
    // of LIVE player doc ids bucketed by playerGroup (quick flow: all "B").
    // Responds with a bare 200/500 (no JSON body), hence responseHandler text.
    randomizePlayers: builder.mutation({
      query: (body) => ({
        url: `secure/randomizeplayers`,
        method: "POST",
        body: body,
        responseHandler: "text",
      }),
    }),
    // The legacy admin's KotC start (SCO-1936 auto-start): seats the initial
    // queue from checked-in teams. body: { accountid, gameid }. Success
    // returns the advance summary JSON; the status flip to GamePlay is a
    // separate client-side Firestore write, exactly like the admin does it.
    kingAdvance: builder.mutation({
      query: (body) => ({
        url: `scoring/kingadvance`,
        method: "POST",
        body: body,
      }),
    }),
    // SCO-2354: Round Robin round placement (the legacy admin dashboard's
    // call). body: { accountid, gameid }. Since prod #168 the route also
    // flips Setup -> GamePlay, so it doubles as the quick RR start.
    rrAdvance: builder.mutation({
      query: (body) => ({
        url: `scoring/advance`,
        method: "POST",
        body: body,
      }),
    }),
    // Switcholio twin of rrAdvance.
    switchAdvance: builder.mutation({
      query: (body) => ({
        url: `scoring/switchadvance`,
        method: "POST",
        body: body,
      }),
    }),
    // The legacy admin's bracket seeding (Finals tab, SCO-1936 auto-start).
    // With playoffcreatebrackettype "First" the server's per-size cases index
    // `playoffarray` ([teamId, teamName, score]) in plain SEED order — the old
    // admin's client-side permutation tables only matter for 65+ brackets —
    // and seedfinalsv2 writes the finals docs AND flips the game status to
    // "Bracket" itself. Bare 200/500 response, hence responseHandler text.
    seedFinals: builder.mutation({
      query: (body) => ({
        url: `secure/seedfinalsv2`,
        method: "POST",
        body: body,
        responseHandler: "text",
      }),
    }),
  }),
});

export const {
  useGetProfileSingleMutation,
  useUpdateProfileSingleMutation,
  useGetStatsMutation,
  useGetStatsTournamentsMutation,
  useParseWizardMutation,
  useUpdateProfileImageMutation,
  useUploadAdMutation,
  useListApiKeysMutation,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetApiKeyUsageMutation,
  useDeleteAdMutation,
  useUploadSeasonLogoMutation,
  usePlayerStatsMutation,
  useUpdateTournamentFormMutation,
  useGetTournamentFormMutation,
  useGetTournamentFormExtraInfoMutation,
  useGetTournamentFormTimezoneLookupMutation,
  useGetTournamentAlertsMutation,
  useSendTournamentAlertMutation,
  useGetTournamentsMutation,
  useCheckAlertCountMutation,
  useGetSeasonMutation,
  useDeleteOrganizerAccountMutation,
  useEndOfYearOrganizerMutation,
  useAddExcludedPlayerMutation,
  useDeleteExcludedPlayerMutation,
  useGetExcludedPlayersMutation,
  useUpdateTournamentTemplateMutation,
  useSendGodPowerAlertMutation,
  useUploadVenuePhotoMutation,
  useListVenuePhotosMutation,
  useDeleteVenuePhotoMutation,
  useManageDashboardsMutation,
  useSearchOrganizersMutation,
  useCheckinAddMutation,
  useNearbySettingsMutation,
  useNearbyStopMutation,
  useNearbyDequeueMutation,
  useCheckinRemoveMutation,
  useSearchClubPlayersMutation,
  useCheckinPreregMutation,
  useRandomizePlayersMutation,
  useKingAdvanceMutation,
  useRrAdvanceMutation,
  useSwitchAdvanceMutation,
  useSeedFinalsMutation,
} = organizerApi;
